import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { RedText } from '../SystemWebhooks.styled';
import { ENDPOINT_HELP_TEXT } from '../constants';
import { AUTH_TYPES, WebhookParamProps } from '../types';

type CreateSystemWebhookModalProps = {
  /** Is create modal open */
  isCreateModalOpen: boolean;
  /** Function to be run when create modal is closed */
  onCreateModalClose: () => void;
  /** Function that creates webhook with passed in params */
  createWebhook: (params: Pick<WebhookParamProps, 'endpoint' | 'authToken' | 'authType'>) => void;
};

export const CreateSystemWebhookModal = (props: CreateSystemWebhookModalProps) => {
  const { isCreateModalOpen, onCreateModalClose, createWebhook } = props;
  const [endpoint, setEndpoint] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // simple check to ensure basic url
  const isValidEndpoint = ['http://', 'https://'].some((prefix) => endpoint.includes(prefix));

  const handleCreateWebhook = () => {
    setIsSubmitted(true);

    if (!isValidEndpoint) {
      return;
    }

    const createWebhookParams = {
      endpoint,
      authType: AUTH_TYPES.NO_AUTH,
    };

    createWebhook(createWebhookParams);
    onCreateModalClose();
  };

  return isCreateModalOpen ? (
    <Modal
      id="createSystemWebhookModal"
      isModalOpen={isCreateModalOpen}
      onClose={onCreateModalClose}
      data-test="createSystemWebhookModal"
    >
      <ModalHeader>Create system webhook</ModalHeader>
      <ModalBody>
        <FormField
          errorText="Please enter a valid URL"
          inputId="webhookEndpoint"
          labelText="Endpoint URL"
          helpText={
            <span>
              Use <RedText data-highlight>{ENDPOINT_HELP_TEXT}</RedText> to encode parameters
            </span>
          }
          hasError={isSubmitted && !isValidEndpoint}
        >
          <InputText
            id="webhookEndpoint"
            hasError={isSubmitted && !isValidEndpoint}
            placeholder="https://www.example.com/webhook"
            onChange={(value) => setEndpoint(value)}
            data-test="createWebhookEndpointInput"
            value={endpoint}
          />
        </FormField>
      </ModalBody>
      <ModalFooter hasCancel onClose={onCreateModalClose}>
        <PrimaryButton data-test="confirmCreateWebhook" onClick={handleCreateWebhook}>
          Create
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  ) : null;
};
